// src/views/fornecedores/FornecedoresList.jsx
import React, { useState, useEffect, useCallback } from "react";
import fornecedoresService, { STORAGE_OPEN_FORNECEDOR } from "../../services/fornecedoresService";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";
import FornecedorModal from "./FornecedorModal";
import DeleteModal from "../../components/ui/DeleteModal";

const PER_PAGE = 10;

const STATS_INIT = { total: 0, ativos: 0, inativos: 0, pj: 0, pf: 0, ramos: 0, comDadosBancarios: 0 };

export default function FornecedoresList() {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("fornecedores");

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [condominios, setCondominios] = useState([]);
  const [stats, setStats]           = useState(STATS_INIT);
  const [ramos, setRamos]           = useState([]);

  const [idCondo, setIdCondo]                 = useState("");
  const [search, setSearch]                   = useState("");
  const [filterSituacao, setFilterSituacao]   = useState("");
  const [filterTpPessoa, setFilterTpPessoa]   = useState("");
  const [filterRamo, setFilterRamo]           = useState("");
  const [page, setPage]                       = useState(1);

  const [showModal, setShowModal]   = useState(false);
  const [editData, setEditData]     = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [serverError, setServerError] = useState(null);

  // Sincroniza com o navbar
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    condominiosService.listar({ limit: 100 }).then((r) => setCondominios(r.data)).catch(() => {});
  }, []);

  const carregarMetadados = useCallback(async () => {
    if (!idCondo) { setStats(STATS_INIT); setRamos([]); return; }
    try {
      const [s, r] = await Promise.all([
        fornecedoresService.stats({ idCondominio: idCondo }),
        fornecedoresService.listarRamos({ idCondominio: idCondo }),
      ]);
      setStats(s.data || STATS_INIT);
      setRamos(r.data || []);
    } catch { /* silencioso */ }
  }, [idCondo]);

  const carregar = useCallback(async () => {
    if (!idCondo) { setData([]); setPagination({ total: 0, totalPages: 1 }); return; }
    setLoading(true); setError(null);
    try {
      const res = await fornecedoresService.listar({
        idCondominio: idCondo, search,
        situacao: filterSituacao, tpPessoa: filterTpPessoa, ramo: filterRamo,
        page, limit: PER_PAGE,
      });
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [idCondo, search, filterSituacao, filterTpPessoa, filterRamo, page]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarMetadados(); }, [carregarMetadados]);

  useEffect(() => {
    if (!idCondo) return;
    const raw = sessionStorage.getItem(STORAGE_OPEN_FORNECEDOR);
    if (!raw) return;
    sessionStorage.removeItem(STORAGE_OPEN_FORNECEDOR);
    fornecedoresService.buscarPorId(raw)
      .then((r) => { setEditData(r.data); setServerError(null); setShowModal(true); })
      .catch(() => setError("Fornecedor não encontrado."));
  }, [idCondo]);

  const condoNome = condominios.find((c) => String(c.id) === String(idCondo))?.nmFantasia || "";
  const totalPages = pagination.totalPages || 1;

  const handleSave = async (form) => {
    setSaving(true); setServerError(null);
    try {
      const payload = { ...form, idCondominio: idCondo };
      if (form.id) await fornecedoresService.atualizar(form.id, payload);
      else         await fornecedoresService.criar(payload);
      setShowModal(false);
      await Promise.all([carregar(), carregarMetadados()]);
    } catch (err) {
      setServerError(err.message || "Erro ao salvar fornecedor.");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true); setError(null);
    try {
      await fornecedoresService.deletar(deleteTarget.id);
      setShowDelete(false); setDeleteTarget(null);
      await Promise.all([carregar(), carregarMetadados()]);
    } catch (err) { setError(err.message); setShowDelete(false); }
    finally { setDeleting(false); }
  };

  const handleToggleSituacao = async (f) => {
    if (!canEdit) return;
    try {
      await fornecedoresService.alterarSituacao(f.id, f.situacao === "ativo" ? "inativo" : "ativo");
      await Promise.all([carregar(), carregarMetadados()]);
    } catch (err) { setError(err.message); }
  };

  const setFilterFromKpi = (situ) => { setFilterSituacao(situ); setPage(1); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-truck me-2" style={{ color: "var(--primary)", fontSize: 22 }} />
            Fornecedores
          </h1>
          <p className="page-subtitle">Gerencie os fornecedores e prestadores de serviço</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <button
            className="btn btn-primary"
            onClick={() => { setEditData(null); setServerError(null); setShowModal(true); }}
            disabled={!idCondo || !canEdit}
            title={!idCondo ? "Selecione um condomínio" : ""}
          >
            <i className="bi bi-plus-lg" /> Novo Fornecedor
          </button>
        </div>
      </div>

      {/* KPIs reais (vindos do backend) e clicáveis */}
      <div className="row g-3 mb-4">
        {[
          { icon: "bi-truck",       cls: "blue",  val: stats.total,    label: "Total",    situ: "" },
          { icon: "bi-check-circle",cls: "green", val: stats.ativos,   label: "Ativos",   situ: "ativo" },
          { icon: "bi-x-circle",    cls: "gray",  val: stats.inativos, label: "Inativos", situ: "inativo" },
          { icon: "bi-buildings",   cls: "orange",val: stats.ramos,    label: "Ramos",    situ: undefined },
        ].map((s) => (
          <div className="col-6 col-md-3" key={s.label}>
            <div
              className="stat-card"
              style={{
                cursor: s.situ !== undefined ? "pointer" : "default",
                boxShadow: s.situ !== undefined && filterSituacao === s.situ ? "0 0 0 2px var(--primary)" : undefined,
              }}
              onClick={s.situ !== undefined ? () => setFilterFromKpi(s.situ) : undefined}
              title={s.situ !== undefined ? "Clique para filtrar" : ""}
            >
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`} /></div>
              <div>
                <div className="stat-value">{s.val ?? 0}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        {[
          { icon: "bi-building", cls: "blue",   val: stats.pj, label: "Pessoa Jurídica" },
          { icon: "bi-person",   cls: "orange", val: stats.pf, label: "Pessoa Física" },
          { icon: "bi-bank",     cls: "green",  val: stats.comDadosBancarios, label: "Com PIX/Banco" },
        ].map((s) => (
          <div className="col-6 col-md-4" key={s.label}>
            <div className="stat-card">
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`} /></div>
              <div>
                <div className="stat-value">{s.val ?? 0}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header-custom">
          <div className="search-wrapper">
            <i className="bi bi-search" />
            <input
              className="search-input"
              placeholder="Buscar por nome, CNPJ/CPF, ramo, e-mail ou contato..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select
              className="form-select form-select-sm" style={{ width: "auto", fontSize: 13 }}
              value={filterSituacao} onChange={(e) => { setFilterSituacao(e.target.value); setPage(1); }}
            >
              <option value="">Todos</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>
            <select
              className="form-select form-select-sm" style={{ width: "auto", fontSize: 13 }}
              value={filterTpPessoa} onChange={(e) => { setFilterTpPessoa(e.target.value); setPage(1); }}
            >
              <option value="">Todos os tipos</option>
              <option value="Juridica">Pessoa Jurídica</option>
              <option value="Fisica">Pessoa Física</option>
            </select>
            <select
              className="form-select form-select-sm" style={{ width: "auto", fontSize: 13 }}
              value={filterRamo} onChange={(e) => { setFilterRamo(e.target.value); setPage(1); }}
            >
              <option value="">Todos os ramos</option>
              {ramos.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { carregar(); carregarMetadados(); }}>
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger m-3 mb-0" style={{ fontSize: 13 }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
            <button type="button" className="btn-close float-end" onClick={() => setError(null)} style={{ fontSize: 10 }} />
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-custom">
            <thead>
              <tr>
                <th>#</th><th>Razão Social</th><th>Tipo</th><th>CNPJ / CPF</th>
                <th>Ramo</th><th>Contato</th><th>Vínculos</th><th>Situação</th>
                <th style={{ width: 140 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-5">
                  <span className="spinner-border spinner-border-sm text-primary me-2" />
                  <span style={{ color: "var(--gray-500)" }}>Carregando...</span>
                </td></tr>
              ) : !idCondo ? (
                <tr><td colSpan={9} className="text-center py-5" style={{ color: "var(--gray-400)" }}>
                  <i className="bi bi-buildings" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                  Selecione um condomínio
                </td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-5" style={{ color: "var(--gray-400)" }}>
                  <i className="bi bi-search" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                  Nenhum fornecedor encontrado
                </td></tr>
              ) : data.map((f, idx) => (
                <tr key={f.id}>
                  <td style={{ color: "var(--gray-400)", fontSize: 12 }}>{(page - 1) * PER_PAGE + idx + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: "var(--gray-800)" }}>{f.nmReduzido || f.nmRazaoSocial}</div>
                    {f.nmReduzido && <div style={{ fontSize: 11.5, color: "var(--gray-400)" }}>{f.nmRazaoSocial}</div>}
                  </td>
                  <td>
                    <span style={{
                      background: f.tpPessoa === "Fisica" ? "#FFF7ED" : "#EFF6FF",
                      color: f.tpPessoa === "Fisica" ? "#C2410C" : "var(--primary)",
                      padding: "2px 10px", borderRadius: 12, fontSize: 11.5, fontWeight: 600,
                    }}>
                      {f.tpPessoa === "Fisica" ? "PF" : "PJ"}
                    </span>
                  </td>
                  <td style={{ fontFamily: "monospace", fontSize: 12.5 }}>{f.cpf_cnpj || "—"}</td>
                  <td style={{ fontSize: 13 }}>{f.ramoAtividade || "—"}</td>
                  <td style={{ fontSize: 12.5 }}>
                    <div>{f.celular || f.telefone || "—"}</div>
                    {f.contato && <div style={{ fontSize: 11, color: "var(--gray-500)" }}><i className="bi bi-person me-1" />{f.contato}</div>}
                    {f.email && <div style={{ fontSize: 11, color: "var(--gray-500)" }}><i className="bi bi-envelope me-1" />{f.email}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {(f.qtdContasPagar > 0 || f.qtdDespRec > 0) ? (
                      <div className="d-flex flex-column gap-1">
                        {f.qtdContasPagar > 0 && (
                          <span title="Contas a pagar">
                            <i className="bi bi-cash-coin me-1" style={{ color: "var(--primary)" }} />{f.qtdContasPagar}
                          </span>
                        )}
                        {f.qtdDespRec > 0 && (
                          <span title="Despesas recorrentes">
                            <i className="bi bi-arrow-repeat me-1" style={{ color: "#7c3aed" }} />{f.qtdDespRec}
                          </span>
                        )}
                      </div>
                    ) : <span style={{ color: "var(--gray-400)" }}>—</span>}
                  </td>
                  <td>
                    <span className={`badge-status ${f.situacao === "ativo" ? "badge-ativo" : "badge-inativo"}`}>
                      <i className="bi bi-circle-fill" style={{ fontSize: 7 }} />{f.situacao === "ativo" ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="btn btn-outline-secondary btn-sm btn-icon"
                        title={f.situacao === "ativo" ? "Inativar" : "Reativar"}
                        onClick={() => handleToggleSituacao(f)}
                        disabled={!canEdit}
                      >
                        <i className={`bi ${f.situacao === "ativo" ? "bi-toggle-on" : "bi-toggle-off"}`} />
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm btn-icon"
                        title="Editar"
                        onClick={() => { setEditData(f); setServerError(null); setShowModal(true); }}
                        disabled={!canEdit}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm btn-icon"
                        title="Excluir"
                        onClick={() => { setDeleteTarget(f); setShowDelete(true); }}
                        disabled={!canEdit}
                      >
                        <i className="bi bi-trash3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination-wrapper">
          <span className="pagination-info">
            {pagination.total === 0 ? "Nenhum registro" : `Exibindo ${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, pagination.total)} de ${pagination.total} registros`}
          </span>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p - 1)}><i className="bi bi-chevron-left" /></button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p + 1)}><i className="bi bi-chevron-right" /></button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <FornecedorModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
        condominioNome={condoNome}
        serverError={serverError}
        clearServerError={() => setServerError(null)}
      />
      <DeleteModal
        show={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.nmReduzido || deleteTarget?.nmRazaoSocial}
        loading={deleting}
      />
    </div>
  );
}
